import { memo, useCallback, useMemo } from 'react';

import { ClockIcon, ImagesIcon, StarIcon } from '@air/design-icons';

import { ItemPickerSidebarGeneralLibraryRow } from '~/components/ItemPicker/ItemPickerSidebarGeneralLibraryRow';
import { ItemPickerSidebarLibraryRow } from '~/components/ItemPicker/ItemPickerSidebarLibraryRow';
import { ItemPickerSidebarNavItem } from '~/components/ItemPicker/ItemPickerSidebarNavItem';
import { GENERAL_LIBRARY_ID } from '~/components/ItemPicker/itemPickerConstants';
import {
  type ItemPickerBoardSelectParams,
  type ItemPickerLibrarySelectParams,
  type ItemPickerNavView,
} from '~/components/ItemPicker/itemPickerTypes';
import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';

export type ItemPickerSidebarProps = {
  navView: ItemPickerNavView;
  onNavViewChange: (view: ItemPickerNavView) => void;
};

export const ItemPickerSidebar = memo(({ navView, onNavViewChange }: ItemPickerSidebarProps) => {
  const { data: librariesData } = useLibraries();
  const libraries = useMemo(() => librariesData?.pages.flatMap((page) => page.data) ?? [], [librariesData]);

  const activeBoardId = navView.kind === 'board' ? navView.boardId : undefined;
  const activeLibraryId = navView.kind === 'library' ? navView.libraryId : undefined;

  const handleAllAssetsClick = useCallback(() => {
    onNavViewChange({ kind: 'all' });
  }, [onNavViewChange]);

  const handleFavoritesClick = useCallback(() => {
    onNavViewChange({ kind: 'favorites' });
  }, [onNavViewChange]);

  const handleRecentClick = useCallback(() => {
    onNavViewChange({ kind: 'recent' });
  }, [onNavViewChange]);

  const handleSelectLibrary = useCallback(
    ({ libraryId, label }: ItemPickerLibrarySelectParams) => {
      onNavViewChange({ kind: 'library', libraryId, label });
    },
    [onNavViewChange],
  );

  const handleSelectBoard = useCallback(
    ({ board, libraryId }: ItemPickerBoardSelectParams) => {
      onNavViewChange({ kind: 'board', boardId: board.id, libraryId, label: board.title });
    },
    [onNavViewChange],
  );

  return (
    <div
      className="flex w-[220px] shrink-0 flex-col overflow-y-auto ads:border-default border-gray-4 border-r px-3 py-4"
      data-testid="ITEM_PICKER_SIDEBAR"
    >
      <div className="flex flex-col gap-px">
        <ItemPickerSidebarNavItem
          icon={ImagesIcon}
          isActive={navView.kind === 'all'}
          itemId="all"
          label="Search"
          onClick={handleAllAssetsClick}
        />
        <ItemPickerSidebarNavItem
          icon={StarIcon}
          isActive={navView.kind === 'favorites'}
          itemId="favorites"
          label="My favorites"
          onClick={handleFavoritesClick}
        />
        <ItemPickerSidebarNavItem
          icon={ClockIcon}
          isActive={navView.kind === 'recent'}
          itemId="recent"
          label="Recently added"
          onClick={handleRecentClick}
        />
      </div>

      <div className="mt-2 flex flex-col">
        <div className="px-2 py-1">
          <span className="font-semibold ads:text-metadata text-12 text-gray-9">Libraries</span>
        </div>
        <ItemPickerSidebarGeneralLibraryRow
          activeBoardId={activeBoardId}
          isActive={activeLibraryId === GENERAL_LIBRARY_ID}
          onSelectBoard={handleSelectBoard}
          onSelectLibrary={handleSelectLibrary}
        />
        {libraries.map((library) => (
          <ItemPickerSidebarLibraryRow
            activeBoardId={activeBoardId}
            isActive={activeLibraryId === library.id}
            key={library.id}
            library={library}
            onSelectBoard={handleSelectBoard}
            onSelectLibrary={handleSelectLibrary}
          />
        ))}
      </div>
    </div>
  );
});

ItemPickerSidebar.displayName = 'ItemPickerSidebar';
